import './search.scss'
import { Debounce } from '@common/classes/debounce'
import { SearchInputBox } from '@common/components/searchInputBox/SearchInputBox'
import type { ChangeEvent, FC } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useDisableScrollForModal } from '@/common/hooks/useDisableScrollForModal'
import { usePreventScroll } from '@/common/hooks/usePreventScroll'
import { setSearchResAsync } from '@/content/sections/header/subcomponents/search/helpers'
import type { searchResponse } from '@/content/sections/header/subcomponents/search/subcomponents/searchResponse'
import { SearchResults } from '@/content/sections/header/subcomponents/search/subcomponents/SearchResults'
import { cmsDataService } from '@/content/services/cms.data.service'
import magnifier from '../../../../../../assets/img/magnifier.svg'

export const Search: FC<{ toggleSearchOpenCallback: (bool) => void }> = props => {
    const { toggleSearchOpenCallback } = props
    const [searchOpen, setSearchOpen] = useState<boolean>(false)
    const [transitioning, setTransitioning] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [isShowClearButton, setIsShowClearButton] = useState<boolean>(true)

    const { closeSearchLabel } = cmsDataService.get().searchConfiguration

    const [searchResponse, setSearchResponse] = useState<searchResponse>({
        results: [],
        showEmptyState: true,
    })

    const debounce = new Debounce(750)

    const inputRef = useRef<HTMLInputElement>(null)
    const navRef = useRef<HTMLElement>(null)

    usePreventScroll(searchOpen)
    useDisableScrollForModal(searchOpen, ['.header-search-container'], '.search-results')

    const closeSearch = useCallback(() => {
        setSearchOpen(false)
        setTransitioning(true)
        clearSearch()

        setTimeout(function () {
            setTransitioning(false)
        }, 500)

        setTimeout(function () {
            toggleSearchOpenCallback(false)
            setSearchResponse({
                results: [],
                showEmptyState: true,
            })
        }, 510)
    }, [toggleSearchOpenCallback])

    const onClickSearchHandler = () => {
        toggleSearchOpenCallback(true)

        setTimeout(function () {
            setTransitioning(true)
        }, 500)

        setTimeout(function () {
            setSearchOpen(true)
        }, 510)

        // Slight delay as it can't focus instantly after opening the search box
        setTimeout(() => {
            inputRef?.current?.focus()
        }, 610)
    }

    const onClickCancelHandler = () => {
        closeSearch()
        setIsShowClearButton(false)
    }

    const onClickClear = () => {
        clearSearch()
        setSearchResponse({
            results: [],
            showEmptyState: true,
        })
        inputRef?.current?.focus()
    }

    const clearSearch = () => {
        if (inputRef?.current?.value) inputRef.current.value = ''
    }

    const onQueryChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        debounce.execute(() => void setSearchResAsync(e, setSearchResponse, setLoading))
    }

    const searchBoxClass = searchOpen ? 'active-search' : ''
    const transitionClass = transitioning ? 'transitioning' : ''

    return (
        <div className={`header-search-container ${transitionClass}`}>
            <nav className={`header-search ${searchBoxClass}`} ref={navRef} data-testid="header-search">
                <img alt="magnifier-icon" src={magnifier} className="magnifier-icon" onClick={onClickSearchHandler} />

                <div className="search-bar">
                    <div className="search-input">
                        <SearchInputBox
                            id="search-input"
                            ref={inputRef}
                            onQueryChangeHandler={onQueryChangeHandler}
                            onClickClear={onClickClear}
                            showClearButton={isShowClearButton}
                            setShowClearButton={setIsShowClearButton}
                        />
                    </div>
                    <div className="search-cancel">
                        <span className="cancel-search-input" onClick={onClickCancelHandler}>
                            {closeSearchLabel}
                        </span>
                    </div>
                </div>
            </nav>

            <div className={`search-results ${searchBoxClass}`}>
                <div className="loading-area">{loading && <div className="loading-bar" />}</div>
                <SearchResults response={searchResponse} onClick={closeSearch} />
            </div>
        </div>
    )
}
